"use client";

import { useEffect, useRef, useState } from "react";
import { useVisibleInterval } from "./use-visible-interval";

export type TvDisplayConnection = "live" | "reconnecting";

function isAbortError(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    "name" in error &&
    (error as { name?: unknown }).name === "AbortError"
  );
}

/**
 * Replaces the latest snapshot on each tick. A failed poll keeps the last
 * good frame and retries, so a TV can stay up for hours without stacking
 * history or abandoned requests.
 */
export function useTvDisplayPoll<T>(options: {
  initial: T;
  load: (signal: AbortSignal) => Promise<T>;
  intervalMs: number;
  enabled?: boolean;
}): { snapshot: T; connection: TvDisplayConnection } {
  const { initial, load, intervalMs, enabled = true } = options;
  const [snapshot, setSnapshot] = useState(initial);
  const [connection, setConnection] = useState<TvDisplayConnection>("live");
  const loadRef = useRef(load);
  loadRef.current = load;
  const abortRef = useRef<AbortController | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
      abortRef.current = null;
    };
  }, []);

  useVisibleInterval(() => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    void loadRef
      .current(controller.signal)
      .then((next) => {
        if (!mountedRef.current || controller.signal.aborted) {
          return;
        }
        setSnapshot(next);
        setConnection("live");
      })
      .catch((error: unknown) => {
        if (
          !mountedRef.current ||
          controller.signal.aborted ||
          isAbortError(error)
        ) {
          return;
        }
        setConnection("reconnecting");
      });
  }, intervalMs, enabled);

  return { snapshot, connection };
}
